import React, { useState, useRef, useEffect } from 'react';
import { HeartIcon, MessageIcon, LocationIcon } from './Icons';
import { ImageWithPlaceholder, AvatarWithFallback, UserAvatarPlaceholder } from './Placeholders';
import { apiService, useAuth } from '../auth'; // Importing the API service and useAuth hook from auth.js

// Listing Card Component
export const ListingCard = ({ listing, onLike, onContact, onViewDetails }) => {
  const { user } = useAuth(); // Obtenir l'utilisateur connecté
  const [isLiked, setIsLiked] = useState(listing?.is_favorited || false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isContacting, setIsContacting] = useState(false);
  const isMountedRef = useRef(true);

  // Cleanup au démontage
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Debug: afficher la structure des images
  console.log('Listing images structure:', listing?.images);

  const handleLike = async () => {
    if (isProcessing || !isMountedRef.current) return;
    
    setIsProcessing(true);
    try {
      if (isLiked) {
        const result = await apiService.removeFromFavorites(listing.id);
        if (result.success && isMountedRef.current) {
          setIsLiked(false);
          if (onLike) onLike(listing.id, false);
        }
      } else {
        const result = await apiService.addToFavorites(listing.id);
        if (result.success && isMountedRef.current) {
          setIsLiked(true);
          if (onLike) onLike(listing.id, true);
        }
      }
    } catch (error) {
      console.error('Error updating favorite:', error);
    } finally {
      if (isMountedRef.current) {
        setIsProcessing(false);
      }
    }
  };

  const handleContact = async () => {
    if (isContacting || !user) return;
    
    if (!user) {
      alert('Vous devez être connecté pour contacter le vendeur');
      return;
    }

    // Vérifier si l'utilisateur ne tente pas de se contacter lui-même
    if (listing.seller_id === user.id || listing.user_id === user.id) {
      alert('Vous ne pouvez pas vous contacter vous-même');
      return;
    }
    
    console.log('� Début du processus de contact pour l\'annonce:', listing.id);
    setIsContacting(true);
    
    try {
      console.log('� Appel de contactSeller...');
      const result = await apiService.contactSeller(listing.id);
      console.log('� Résultat de contactSeller:', result);
      
      if (result.success) {
        console.log('✅ Conversation créée avec succès');
        console.log('🎯 Redirection vers la conversation avec:', result.data);
        
        // Rediriger immédiatement vers la conversation
        if (onContact) {
          onContact(result.data);
        } else {
          console.error('❌ Callback onContact non disponible');
          alert('Erreur: fonction de redirection non disponible');
        }
      } else {
        console.error('❌ Échec de la création de conversation:', result.message);
        alert('Erreur lors de la création de la conversation: ' + result.message);
      }
    } catch (error) {
      console.error('❌ Erreur lors du contact:', error);
      alert('Erreur lors de la création de la conversation');
    } finally {
      setIsContacting(false);
    }
  };

  // Adapter les données du backend aux props du composant
  const getImageUrl = (images) => {
    if (!images || images.length === 0) return null;
    
    const firstImage = images[0];
    console.log('First image structure:', firstImage);
    
    // Si c'est un objet CloudinaryImage
    if (typeof firstImage === 'object' && firstImage !== null) {
      // Priorité à secure_url, puis url
      let url = firstImage.secure_url || firstImage.url;
      
      if (url) {
        // Nettoyer l'URL si elle contient des paramètres problématiques
        url = url.replace(/\.auto$/, '');
        url = url.replace(/q_auto:best/g, 'q_auto');
        url = url.replace(/f_auto:best/g, 'f_auto');
        
        console.log('Cleaned URL:', url);
        return url;
      }
    }
    
    // Si c'est juste une URL string (anciens formats)
    if (typeof firstImage === 'string') {
      let url = firstImage;
      // Nettoyer l'URL
      url = url.replace(/\.auto$/, '');
      url = url.replace(/q_auto:best/g, 'q_auto');
      url = url.replace(/f_auto:best/g, 'f_auto');
      
      console.log('Cleaned string URL:', url);
      return url;
    }
    
    console.log('No valid image URL found');
    return null;
  };

  // Protection contre les données manquantes
  if (!listing) {
    return null;
  }

  const displayImage = getImageUrl(listing.images) || listing.image || null;
  
  const displaySeller = listing.seller 
    ? `${listing.seller.first_name} ${listing.seller.last_name}`
    : listing.seller_name || 'Vendeur';
  
  const displaySellerAvatar = listing.seller?.avatar_url || 
    listing.sellerAvatar || null;

  const displayPrice = typeof listing.price === 'number' 
    ? listing.price.toFixed(2) 
    : listing.price;

  const displayTime = listing.postedTime || 
    (listing.created_at ? formatTimeAgo(listing.created_at) : 'Récemment');

  // Vérifier si l'utilisateur connecté est l'auteur de l'annonce
  const isOwnListing = user && listing && (
    (listing.seller && user.id === listing.seller.id) || 
    (listing.seller_id && user.id === listing.seller_id) ||
    (listing.owner_id && user.id === listing.owner_id)
  );

  const handleCardClick = () => {
    if (onViewDetails) {
      onViewDetails(listing.id);
    }
  };

  return (
    <div 
      className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden cursor-pointer hover:shadow-md transition-shadow"
      onClick={handleCardClick}
    >
      <div className="relative">
        <ImageWithPlaceholder
          src={displayImage}
          alt={listing.title}
          className="w-full h-48 object-cover"
          fallbackSrc="https://via.placeholder.com/400x300/f3f4f6/9ca3af?text=Image+non+disponible"
        >
          <button 
            onClick={(e) => {
              e.stopPropagation();
              handleLike();
            }}
            disabled={isProcessing}
            className={`absolute top-2 right-2 p-2 rounded-full transition-all duration-200 ${
              isLiked ? 'bg-red-500 text-white shadow-lg' : 'bg-white/90 text-gray-600 backdrop-blur-sm'
            } ${isProcessing ? 'opacity-50 cursor-not-allowed' : 'hover:scale-105 hover:shadow-lg'}`}
          >
            {isProcessing ? (
              <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            ) : (
              <HeartIcon filled={isLiked} />
            )}
          </button>
          <div className="absolute bottom-2 left-2 bg-black/70 backdrop-blur-sm text-white px-2 py-1 rounded text-xs">
            {displayTime}
          </div>
        </ImageWithPlaceholder>
      </div>
      
      <div className="p-3">
        <h3 className="font-semibold text-gray-900 mb-1">{listing.title}</h3>
        <p className="text-lg font-bold text-blue-600 mb-2">€{displayPrice}</p>
        
        <div className="flex items-center gap-1 text-gray-500 text-sm mb-3">
          <LocationIcon />
          <span>{listing.location}</span>
        </div>
        
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AvatarWithFallback
              src={displaySellerAvatar}
              alt={displaySeller}
              fallbackText={displaySeller}
              className="w-6 h-6 rounded-full object-cover"
              showInitials={true}
            />
            <span className="text-sm text-gray-600">{displaySeller}</span>
          </div>
          
          {/* Afficher le bouton Contact seulement si ce n'est pas l'annonce de l'utilisateur connecté */}
          {!isOwnListing && (
            <button 
              onClick={(e) => {
                e.stopPropagation();
                handleContact();
              }}
              disabled={isContacting}
              className="bg-blue-600 text-white px-3 py-1 rounded-full text-sm flex items-center gap-1 hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {isContacting ? (
                <>
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Contact...
                </>
              ) : (
                <>
                  <MessageIcon />
                  Contact
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Fonction utilitaire pour formater le temps
const formatTimeAgo = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInMs = now - date;
  const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
  const diffInHours = Math.floor(diffInMinutes / 60);
  const diffInDays = Math.floor(diffInHours / 24);

  if (diffInMinutes < 60) {
    return `${diffInMinutes}min ago`;
  } else if (diffInHours < 24) {
    return `${diffInHours}h ago`;
  } else if (diffInDays < 7) {
    return `${diffInDays}d ago`;
  } else {
    return date.toLocaleDateString();
  }
};

// Listings Grid Component
export const ListingsGrid = ({ listings, onLike, onContact, onViewDetails }) => (
  <div className="p-4">
    <h2 className="text-xl font-bold text-gray-900 mb-4">Recent Listings</h2>
    <div className="space-y-4">
      {listings.map(listing => (
        <ListingCard 
          key={listing.id}
          listing={listing}
          onLike={onLike}
          onContact={onContact}
          onViewDetails={onViewDetails}
        />
      ))}
    </div>
  </div>
);
